import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Mail, Lock, User } from "lucide-react";
import { useAuth } from "../AuthContext";
import { AuthShell } from "./Login";
import { GoogleAuthButton } from "../components/GoogleAuthButton";

const Field = ({ icon: Icon, ...props }) => (
  <div className="relative">
    <Icon size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500" />
    <input {...props} className="cc-input w-full rounded-xl pl-10 pr-4 py-3 text-sm" />
  </div>
);

export default function Signup() {
  const { signup } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) return toast.error("Password must be at least 6 characters");
    if (!agreed) return toast.error("Please agree to the Terms of Service and Privacy Policy");
    setLoading(true);
    const res = await signup(name, email, password, agreed);
    setLoading(false);
    if (res.ok) {
      toast.success("Account created — welcome to ArcaCentum Credit!");
      navigate("/credit/app");
    } else {
      toast.error(res.error);
    }
  };

  return (
    <AuthShell
      title="Create your free account"
      subtitle="Start building business & personal credit"
      footer={<>Already have an account? <Link to="/credit/login" className="text-blue-300 hover:text-blue-200">Log in</Link></>}
    >
      <GoogleAuthButton mode="register" label="Sign up with Google" testid="google-signup-btn" />
      <p className="text-center text-[11px] text-slate-500 mt-2">
        By continuing with Google you agree to our <Link to="/terms" target="_blank" className="text-blue-300 hover:text-blue-200">Terms</Link> and <Link to="/privacy" target="_blank" className="text-blue-300 hover:text-blue-200">Privacy Policy</Link>.
      </p>
      <div className="flex items-center gap-3 my-5">
        <div className="h-px flex-1 bg-white/[0.06]" />
        <span className="text-xs text-slate-500">or</span>
        <div className="h-px flex-1 bg-white/[0.06]" />
      </div>
      <form onSubmit={submit} className="space-y-3.5">
        <Field icon={User} type="text" required placeholder="Full name" value={name} onChange={(e) => setName(e.target.value)} data-testid="signup-name" />
        <Field icon={Mail} type="email" required placeholder="Email address" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="signup-email" />
        <Field icon={Lock} type="password" required placeholder="Password (min 6 characters)" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="signup-password" />
        <label className="flex items-start gap-2.5 cursor-pointer select-none pt-1">
          <input type="checkbox" required checked={agreed} onChange={(e) => setAgreed(e.target.checked)} data-testid="signup-terms-checkbox"
            className="mt-0.5 h-4 w-4 shrink-0 rounded border-white/20 bg-white/5 accent-blue-500" />
          <span className="text-xs text-slate-400 leading-relaxed">
            I agree to the{" "}
            <Link to="/terms" target="_blank" className="text-blue-300 hover:text-blue-200 underline underline-offset-2">Terms of Service</Link>,{" "}
            <Link to="/privacy" target="_blank" className="text-blue-300 hover:text-blue-200 underline underline-offset-2">Privacy Policy</Link>, and{" "}
            <Link to="/refund-policy" target="_blank" className="text-blue-300 hover:text-blue-200 underline underline-offset-2">Refund Policy</Link>.
          </span>
        </label>
        <button type="submit" disabled={loading} data-testid="signup-submit" className="cc-btn-primary flex w-full items-center justify-center gap-2 rounded-xl py-3 text-sm font-500 disabled:opacity-60">
          {loading ? "Creating account…" : <>Start Free <ArrowRight size={16} /></>}
        </button>
        <p className="text-center text-xs text-slate-500 pt-1">No credit card required.</p>
      </form>
    </AuthShell>
  );
}
